/**
 * ContractDetail — Detail view for a single contract.
 * Shows core contract fields in organized sections + custom fields via DynamicDetailRenderer.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Stack, Typography, Chip, Divider, IconButton,
  Tooltip, Paper, Grid, Button, LinearProgress, Alert, Card, CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ArticleIcon from '@mui/icons-material/Article';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import PaymentsIcon from '@mui/icons-material/Payments';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import apiService from '../../services/apiServices';
import dateFormats from '../../services/dateFormats';
import useCustomFields from '../../hooks/useCustomFields';
import ModuleCustomFields from '../dynamic/ModuleCustomFields';
import { alpha, useTheme } from '@mui/material/styles';

// ── Core field component ──────────────────────────────────────────────────────
function DetailField({ label, value, span = 1 }) {
  const isEmpty = value === undefined || value === null || value === '' || value === '—';
  return (
    <Grid item xs={12} sm={6 * span}>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ display: 'block', mb: 0.25, textTransform: 'uppercase', fontSize: '0.67rem', letterSpacing: '0.04em' }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.84rem', color: isEmpty ? 'text.disabled' : 'text.primary' }}>
          {isEmpty ? '—' : value}
        </Typography>
      </Box>
    </Grid>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function DetailSection({ title, icon, children }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ px: 2, py: '10px', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 16, color: 'primary.main' } })}
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>{title}</Typography>
      </Box>
      <CardContent sx={{ pt: 2, pb: '16px !important' }}>
        <Grid container spacing={2.5}>
          {children}
        </Grid>
      </CardContent>
    </Card>
  );
}

const CONTRACT_TYPE_LABELS = {
  indefinite:   'Kontratë pa Afat (e Pacaktuar)',
  fixed_term:   'Kontratë me Afat të Caktuar',
  probationary: 'Kontratë Provuese',
  part_time:    'Kontratë me Kohë të Pjesshme',
  seasonal:     'Kontratë Sezonale',
  internship:   'Kontratë Praktike / Internship',
};

const WORK_LOCATION_LABELS = {
  office: 'Zyrë',
  remote: 'Remote / Distancë',
  hybrid: 'Hibride',
  field:  'Terren',
};

export default function ContractDetail() {
  const theme = useTheme();
  const { id } = useParams();

  const [contract, setContract] = useState(null);
  const [worker, setWorker]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const {
    fields: customFields,
    sections: customSections,
    values: customValues,
    loading: customFieldsLoading,
  } = useCustomFields('contracts', id);

  useEffect(() => {
    if (!id) { setLoading(false); setError('ID e kontratës mungon'); return; }
    let cancelled = false;

    (async () => {
      try {
        const cRes = await apiService().query('getContract', 'id=' + id);
        if (!cancelled) {
          if (cRes?.success && cRes.data?.[0]) {
            const c = cRes.data[0];
            const normalized = {
              Id:              c.Id          ?? c.id,
              WorkerId:        c.WorkerId    ?? c.worker_id,
              ContractType:    c.ContractType ?? c.contract_type ?? '',
              StartDate:       c.StartDate   ?? c.start_date,
              EndDate:         c.EndDate     ?? c.end_date,
              Position:        c.Position    ?? c.position,
              Department:      c.Department  ?? c.department,
              WorkLocation:    c.WorkLocation ?? c.work_location,
              WorkHoursWeek:   c.WorkHoursWeek ?? c.work_hours_week,
              BaseSalary:      c.BaseSalary  ?? c.base_salary ?? c.Salary,
              HourSalary:      c.HourSalary  ?? c.hour_salary,
              HourSalaryWeekend: c.HourSalaryWeekend ?? c.hour_salary_weekend,
              HourSalaryHoliday: c.HourSalaryHoliday ?? c.hour_salary_holiday,
              BankAccount:     c.BankAccount ?? c.bank_account,
              BankName:        c.BankName    ?? c.bank_name,
              SalaryType:      c.SalaryType  ?? c.salary_type,
              TermsOfContract: c.TermsOfContract ?? c.terms_of_contract,
              Notes:           c.Notes       ?? c.notes,
              Status:          c.Status      ?? c.status,
              CreatedAt:       c.CreatedAt   ?? c.created_at,
            };
            setContract(normalized);

            // Load worker details
            if (normalized.WorkerId) {
              const wRes = await apiService().query('getWorker', 'id=' + normalized.WorkerId);
              if (!cancelled && wRes?.success && wRes.data?.[0]) {
                const w = wRes.data[0];
                setWorker({
                  FirstName: w.FirstName ?? w.first_name ?? w.firstname,
                  LastName:  w.LastName  ?? w.last_name  ?? w.lastname,
                  Email:     w.Email     ?? w.email,
                  Phone:     w.Phone     ?? w.phone,
                  Image:     w.Image     ?? w.image,
                });
              }
            }
          } else {
            setError('Kontrata nuk u gjet.');
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Gabim gjatë ngarkimit.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id]);

  // ── Derived values ────────────────────────────────────────────────────────
  const workerName = worker
    ? [worker.FirstName, worker.LastName].filter(Boolean).join(' ')
    : '—';

  const contractTypeLabel = CONTRACT_TYPE_LABELS[contract?.ContractType] || contract?.ContractType || '—';
  const workLocationLabel = WORK_LOCATION_LABELS[contract?.WorkLocation] || contract?.WorkLocation || '—';

  const now = new Date();
  const endDate = contract?.EndDate ? new Date(contract.EndDate) : null;
  const daysLeft = endDate ? Math.floor((endDate - now) / (1000 * 60 * 60 * 24)) : null;
  const statusColor =
    daysLeft === null ? '#3E8FA3'
    : daysLeft < 0    ? '#ef4444'
    : daysLeft <= 30  ? '#f59e0b'
    : '#10b981';
  const statusLabel =
    daysLeft === null ? 'Aktive (pa afat)'
    : daysLeft < 0    ? 'Skaduar'
    : daysLeft <= 30  ? `Skadon në ${daysLeft} ditë`
    : 'Aktive';

  if (loading) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <LinearProgress sx={{ borderRadius: 999 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button component={Link} to="/contractlist" startIcon={<ArrowBackIcon />} sx={{ mt: 2, textTransform: 'none' }}>
          Kthehu te lista
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 0, sm: 0.5 }, py: { xs: 1, sm: 1.5 } }}>
      <Stack spacing={2}>

        {/* ── Header toolbar ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Kthehu">
              <IconButton
                component={Link}
                to="/contractlist"
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
              >
                <ArrowBackIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Box sx={{
              width: 32, height: 32, borderRadius: '9px',
              background: 'linear-gradient(135deg, #0369A1 0%, #0284C7 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ArticleIcon sx={{ color: '#fff', fontSize: 17 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={800} lineHeight={1.15} sx={{ fontSize: '0.93rem' }}>
                Kontrata #{id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.69rem' }}>
                {workerName !== '—' ? `${workerName} · ` : ''}{contractTypeLabel}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon sx={{ fontSize: '15px !important' }} />}
            component={Link}
            to={`/contractlist/editcontract/${id}`}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontSize: '0.76rem', px: 1.5, py: 0.6,
              background: 'linear-gradient(135deg, #0369A1 0%, #0284C7 100%)', boxShadow: 'none',
              '&:hover': { boxShadow: '0 4px 12px rgba(3,105,161,0.35)' },
            }}
          >
            Ndrysho
          </Button>
        </Stack>

        {/* ── Contract hero card ── */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ height: 6, background: 'linear-gradient(90deg, #0369A1 0%, #0284C7 50%, #38bdf8 100%)' }} />
          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                    {contractTypeLabel}
                  </Typography>
                  <Box sx={{
                    px: 1, py: '2px', borderRadius: 999, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.07em',
                    bgcolor: alpha(statusColor, 0.12),
                    border: `1.5px solid ${alpha(statusColor, 0.3)}`,
                    color: statusColor,
                  }}>
                    {statusLabel}
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
                  {workerName !== '—' && (
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                      <PersonIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                      <Typography color="text.secondary" sx={{ fontSize: '0.78rem' }}>{workerName}</Typography>
                    </Stack>
                  )}
                  {contract?.Position && (
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                      <WorkOutlineIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                      <Typography color="text.secondary" sx={{ fontSize: '0.78rem' }}>{contract.Position}</Typography>
                    </Stack>
                  )}
                  {contract?.StartDate && (
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                      <CalendarTodayIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                      <Typography color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                        {dateFormats(contract.StartDate)}
                        {contract.EndDate ? ` → ${dateFormats(contract.EndDate)}` : ' → Pa afat'}
                      </Typography>
                    </Stack>
                  )}
                  {contract?.BaseSalary && (
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                      <PaymentsIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                      <Typography color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                        {Number(contract.BaseSalary).toLocaleString('sq-AL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                        {contract.SalaryType ? ` ${contract.SalaryType}` : ''}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Paper>

        {/* ── Core sections ── */}
        <DetailSection title="Informacionet e Kontratës" icon={<ArticleIcon />}>
          <DetailField label="Lloji i kontratës" value={contractTypeLabel} />
          <DetailField label="Pozita" value={contract?.Position} />
          <DetailField label="Vendndodhja" value={workLocationLabel} />
          <DetailField label="Orët / Javë" value={contract?.WorkHoursWeek ? `${contract.WorkHoursWeek}h` : null} />
          <DetailField label="Data e fillimit" value={dateFormats(contract?.StartDate) || contract?.StartDate} />
          <DetailField label="Data e mbarimit" value={contract?.EndDate ? (dateFormats(contract.EndDate) || contract.EndDate) : 'Pa afat'} />
          {contract?.ContractType === 'probationary' && (
            <DetailField label="Muajt e provës" value={contract?.ProbationMonths ? `${contract.ProbationMonths} muaj` : null} />
          )}
          <DetailField label="Statusi" value={statusLabel} />
        </DetailSection>

        <DetailSection title="Paga & Shpërblimet" icon={<PaymentsIcon />}>
          <DetailField
            label="Paga bazë"
            value={
              contract?.BaseSalary
                ? `${Number(contract.BaseSalary).toLocaleString('sq-AL', { minimumFractionDigits: 2 })} € (${contract.SalaryType || 'Bruto'})`
                : null
            }
          />
          <DetailField label="Orë shtesë / Orë (€)" value={contract?.HourSalary ? `${contract.HourSalary} €` : null} />
          <DetailField label="Fundjavë / Orë (€)" value={contract?.HourSalaryWeekend ? `${contract.HourSalaryWeekend} €` : null} />
          <DetailField label="Festë zyrtare / Orë (€)" value={contract?.HourSalaryHoliday ? `${contract.HourSalaryHoliday} €` : null} />
          <DetailField label="IBAN / Llogari" value={contract?.BankAccount} />
          <DetailField label="Banka" value={contract?.BankName} />
        </DetailSection>

        {(contract?.TermsOfContract || contract?.Notes) && (
          <DetailSection title="Kushtet & Shënime" icon={<ArticleIcon />}>
            {contract?.TermsOfContract && <DetailField label="Kushtet e kontratës" value={contract.TermsOfContract} span={2} />}
            {contract?.Notes && <DetailField label="Shënime" value={contract.Notes} span={2} />}
          </DetailSection>
        )}

        {/* ── Custom fields section ── */}
        {!customFieldsLoading && customFields.filter(f => f.is_custom && f.is_active && f.show_in_detail).length > 0 && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ pb: '16px !important' }}>
              <ModuleCustomFields
                moduleKey="contracts"
                fields={customFields}
                sections={customSections}
                values={customValues}
                formType="detail"
                loading={customFieldsLoading}
              />
            </CardContent>
          </Card>
        )}

      </Stack>
    </Box>
  );
}
